import React, { useMemo } from 'react';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FaClock, FaMapMarkerAlt } from 'react-icons/fa';

const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const WEEKDAYS_SHORT_ES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function monthKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthTitle(date) {
  const m = MONTHS_ES[date.getMonth()] || '';
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${date.getFullYear()}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function mondayIndex(jsDay) {
  // JS: 0=Dom..6=Sáb -> L=0..D=6
  return (jsDay + 6) % 7;
}

function monthGridDays(monthDate) {
  const start = startOfMonth(monthDate);
  const end = endOfMonth(monthDate);
  const leading = mondayIndex(start.getDay());
  const total = leading + end.getDate();
  const trailing = (7 - (total % 7)) % 7;
  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let d = 1; d <= end.getDate(); d += 1) {
    cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
  }
  for (let i = 0; i < trailing; i += 1) cells.push(null);
  return cells;
}

function ymdKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function eventDateKey(e) {
  if (!e?.fecha) return null;
  const d = new Date(e.fecha);
  if (!isValidDate(d)) return null;
  return ymdKey(d);
}

function eventMonthKey(e) {
  if (!e?.fecha) return null;
  const d = new Date(e.fecha);
  if (!isValidDate(d)) return null;
  return monthKey(d);
}

function sortByDateTime(a, b) {
  const da = a?.fecha ? new Date(a.fecha).getTime() : 0;
  const db = b?.fecha ? new Date(b.fecha).getTime() : 0;
  if (da !== db) return da - db;
  return String(a?.horaInicio || '').localeCompare(String(b?.horaInicio || ''));
}

function withinSelectedMonths(e, selectedMonths) {
  if (!selectedMonths || selectedMonths.size === 0) return true;
  if (!e?.fecha) return true;
  const d = new Date(e.fecha);
  if (!isValidDate(d)) return true;
  return selectedMonths.has(d.getMonth() + 1);
}

function MonthHeader({ date, light }) {
  return (
    <HStack justify="center" mb={3} mt={8}>
      <Text
        fontSize="lg"
        fontWeight="900"
        color={light ? 'white' : 'gray.800'}
        letterSpacing="0.02em"
        textTransform="uppercase"
      >
        {monthTitle(date)}
      </Text>
    </HStack>
  );
}

function ActuacionChip({ evento, light }) {
  const horaTxt = evento?.horaInicio
    ? `${evento.horaInicio}${evento.horaFin ? `–${evento.horaFin}` : ''}`
    : null;
  const tooltip = (
    <VStack align="start" gap={1}>
      {!!evento?.nombre && <Text fontSize="xs" fontWeight="800">{evento.nombre}</Text>}
      {(horaTxt || evento?.ubicacion) ? (
        <VStack align="start" gap={0}>
          {horaTxt ? (
            <HStack gap={1}>
              <Icon boxSize={3} asChild><FaClock /></Icon>
              <Text fontSize="xs">{horaTxt}</Text>
            </HStack>
          ) : null}
          {evento?.ubicacion ? (
            <HStack gap={1}>
              <Icon boxSize={3} asChild><FaMapMarkerAlt /></Icon>
              <Text fontSize="xs">{evento.ubicacion}</Text>
            </HStack>
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );
  return (
    <Tooltip
      content={tooltip}
      showArrow
      openDelay={250}
      bg={light ? 'gray.900' : 'gray.800'}
      color="white"
      maxW="260px"
      positioning={{
        placement: "top"
      }}
    >
      <Box
        w="full"
        p={2}
        rounded="md"
        bg={light ? 'whiteAlpha.200' : 'gray.50'}
        borderWidth="1px"
        borderColor={light ? 'whiteAlpha.200' : 'gray.100'}
        _hover={{ bg: light ? 'whiteAlpha.300' : 'gray.100' }}
        cursor="default"
      >
        {!!evento?.nombre && (
          <Text fontSize="xs" fontWeight="900" color={light ? 'white' : 'gray.800'} lineClamp={2}>
            {evento.nombre}
          </Text>
        )}
      </Box>
    </Tooltip>
  );
}

function EnsayoChip({ ensayo, light }) {
  const horaTxt = ensayo?.horaInicio ? `${ensayo.horaInicio}–${ensayo.horaFin || ''}`.replace(/–$/, '') : null;
  const titulo = ensayo?.descripcion || ensayo?.grupo?.nombre || 'Ensayo';
  const tooltip = (
    <VStack align="start" gap={1}>
      <Text fontSize="xs" fontWeight="800">{titulo}</Text>
      {(horaTxt || ensayo?.ubicacion) ? (
        <VStack align="start" gap={0}>
          {horaTxt ? (
            <HStack gap={1}>
              <Icon boxSize={3} asChild><FaClock /></Icon>
              <Text fontSize="xs">{horaTxt}</Text>
            </HStack>
          ) : null}
          {ensayo?.ubicacion ? (
            <HStack gap={1}>
              <Icon boxSize={3} asChild><FaMapMarkerAlt /></Icon>
              <Text fontSize="xs">{ensayo.ubicacion}</Text>
            </HStack>
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );
  return (
    <Tooltip
      content={tooltip}
      showArrow
      openDelay={250}
      bg={light ? 'gray.900' : 'gray.800'}
      color="white"
      maxW="260px"
      positioning={{
        placement: "top"
      }}
    >
      <Box
        w="full"
        p={2}
        rounded="md"
        bg={light ? 'whiteAlpha.200' : 'gray.50'}
        borderWidth="1px"
        borderColor={light ? 'whiteAlpha.200' : 'gray.100'}
        _hover={{ bg: light ? 'whiteAlpha.300' : 'gray.100' }}
        cursor="default"
      >
        <Text fontSize="xs" fontWeight="900" color={light ? 'whiteAlpha.900' : 'gray.800'} lineClamp={2}>
          {titulo}
        </Text>
      </Box>
    </Tooltip>
  );
}

function MonthCalendar({ monthDate, acts, ens, light }) {
  const days = monthGridDays(monthDate);
  const actsByDay = useMemo(() => {
    const map = new Map();
    for (const a of acts) {
      const k = eventDateKey(a);
      if (!k) continue;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    }
    for (const v of map.values()) v.sort(sortByDateTime);
    return map;
  }, [acts]);
  const ensByDay = useMemo(() => {
    const map = new Map();
    for (const e of ens) {
      const k = eventDateKey(e);
      if (!k) continue;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    }
    for (const v of map.values()) v.sort(sortByDateTime);
    return map;
  }, [ens]);

  return (
    <Box w="full">
      <SimpleGrid columns={7} gap={2} mb={2}>
        {WEEKDAYS_SHORT_ES.map((d) => (
          <Text key={d} fontSize="xs" fontWeight="800" textAlign="center" color={light ? 'whiteAlpha.800' : 'gray.500'}>
            {d}
          </Text>
        ))}
      </SimpleGrid>
      <SimpleGrid columns={7} gap={2}>
        {days.map((d, idx) => {
          if (!d) {
            return <Box key={`empty-${idx}`} minH="110px" />;
          }
          const k = ymdKey(d);
          const dayActs = actsByDay.get(k) || [];
          const dayEns = ensByDay.get(k) || [];
          const hasAny = dayActs.length > 0 || dayEns.length > 0;
          return (
            <Box
              key={k}
              minH="110px"
              p={2}
              rounded="lg"
              bg={light ? (hasAny ? 'whiteAlpha.200' : 'whiteAlpha.100') : (hasAny ? 'white' : 'gray.50')}
              borderWidth="1px"
              borderColor={light ? (hasAny ? 'whiteAlpha.300' : 'whiteAlpha.200') : (hasAny ? 'gray.200' : 'gray.100')}
            >
              <Flex justify="space-between" align="center" mb={1}>
                <Text fontSize="xs" fontWeight="900" color={light ? 'white' : 'gray.700'}>
                  {d.getDate()}
                </Text>
                {hasAny ? (
                  <Badge bg={light ? 'whiteAlpha.300' : 'purple.100'} color={light ? 'white' : 'purple.700'} fontSize="2xs" px={2} py={0.5} rounded="full">
                    {dayActs.length + dayEns.length}
                  </Badge>
                ) : null}
              </Flex>
              <VStack gap={1} align="stretch">
                {dayActs.slice(0, 2).map((a) => (
                  <ActuacionChip key={`a-${a.id}`} evento={a} light={light} />
                ))}
                {dayEns.slice(0, 1).map((e) => (
                  <EnsayoChip key={`e-${e.id}`} ensayo={e} light={light} />
                ))}
                {(dayActs.length > 2 || dayEns.length > 1) ? (
                  <Text fontSize="2xs" color={light ? 'whiteAlpha.700' : 'gray.500'}>
                    +{Math.max(0, dayActs.length - 2) + Math.max(0, dayEns.length - 1)} más
                  </Text>
                ) : null}
              </VStack>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

function MonthList({ acts, ens, light }) {
  const items = useMemo(() => {
    const out = [];
    for (const a of acts) out.push({ t: 'act', ...a });
    for (const e of ens) out.push({ t: 'ens', ...e });
    out.sort(sortByDateTime);
    return out;
  }, [acts, ens]);

  return (
    <VStack gap={3} align="stretch">
      {items.map((it) => {
        const d = it?.fecha ? new Date(it.fecha) : null;
        const fechaTop = d && isValidDate(d)
          ? d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : null;
        const horaTxt = it?.horaInicio
          ? `${it.horaInicio}${it.horaFin ? `–${it.horaFin}` : ''}`
          : null;
        const title = it.t === 'ens'
          ? (it.descripcion || it.grupo?.nombre || 'Ensayo')
          : (it.nombre || 'Actuación');
        const place = it.t === 'ens' ? it.ubicacion : it.ubicacion;
        return (
          <Box key={`${it.t}-${it.id}`} bg={light ? 'whiteAlpha.200' : 'white'} backdropFilter={light ? 'blur(4px)' : undefined} rounded="xl" p={5} border="1px solid" borderColor={light ? 'whiteAlpha.200' : 'gray.200'}>
            <HStack justify="space-between" align="start" gap={4}>
              <VStack align="start" gap={1} flex={1} minW={0}>
                <HStack gap={2} flexWrap="wrap">
                  {fechaTop ? <Badge bg={light ? 'whiteAlpha.300' : 'purple.100'} color={light ? 'white' : 'purple.700'} fontSize="xs">{fechaTop}</Badge> : null}
                  {horaTxt ? <Badge bg={light ? 'whiteAlpha.300' : 'gray.100'} color={light ? 'white' : 'gray.700'} fontSize="xs">{horaTxt}</Badge> : null}
                </HStack>
                <Text color={light ? 'white' : 'gray.800'} fontWeight="900" fontSize="md" lineClamp={2}>{title}</Text>
                {place ? (
                  <HStack gap={1} maxW="full">
                    <Icon color={light ? 'whiteAlpha.700' : 'gray.500'} boxSize={3} asChild><FaMapMarkerAlt /></Icon>
                    <Text fontSize="sm" color={light ? 'whiteAlpha.800' : 'gray.600'} lineClamp={2}>{place}</Text>
                  </HStack>
                ) : null}
              </VStack>
              <Badge bg={light ? 'whiteAlpha.300' : 'gray.100'} color={light ? 'white' : 'gray.700'} px={3} py={1} rounded="full" fontSize="xs" flexShrink={0}>
                <HStack gap={1}>
                  <Icon boxSize={3} asChild><FaClock /></Icon>
                  <Text>{it.t === 'ens' ? 'Ensayo' : 'Actuación'}</Text>
                </HStack>
              </Badge>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}

export default function MicrositeCalendario({
  msConfig,
  actuaciones = [],
  ensayos = [],
  light = true,
}) {
  const selectedMonths = useMemo(() => {
    const arr = Array.isArray(msConfig?.calendarioMeses) ? msConfig.calendarioMeses : [];
    return new Set(arr.filter((m) => Number.isInteger(m) && m >= 1 && m <= 12));
  }, [msConfig?.calendarioMeses]);

  const actsFiltered = useMemo(
    () => (actuaciones || []).filter((a) => withinSelectedMonths(a, selectedMonths)).slice().sort(sortByDateTime),
    [actuaciones, selectedMonths],
  );
  const ensFiltered = useMemo(
    () => (ensayos || []).filter((e) => withinSelectedMonths(e, selectedMonths)).slice().sort(sortByDateTime),
    [ensayos, selectedMonths],
  );

  const monthKeys = useMemo(() => {
    const keys = new Set();
    for (const a of actsFiltered) {
      const k = eventMonthKey(a);
      if (k) keys.add(k);
    }
    for (const e of ensFiltered) {
      const k = eventMonthKey(e);
      if (k) keys.add(k);
    }
    return Array.from(keys).sort();
  }, [actsFiltered, ensFiltered]);

  const mode = msConfig?.calendarioModo || 'lista';

  return (
    <VStack align="stretch" gap={6}>
      {monthKeys.map((k) => {
        const [yStr, mStr] = String(k).split('-');
        const y = Number(yStr);
        const m = Number(mStr);
        const monthDate = new Date(y, m - 1, 1);
        if (!isValidDate(monthDate)) return null;

        const acts = actsFiltered.filter((a) => eventMonthKey(a) === k);
        const ens = ensFiltered.filter((e) => eventMonthKey(e) === k);
        if (acts.length === 0 && ens.length === 0) return null;

        return (
          <Box key={k} w="full">
            <MonthHeader date={monthDate} light={light} />
            {mode === 'calendario' ? (
              <MonthCalendar monthDate={monthDate} acts={acts} ens={ens} light={light} />
            ) : (
              <MonthList acts={acts} ens={ens} light={light} />
            )}
          </Box>
        );
      })}
      {monthKeys.length === 0 ? (
        <Box>
          <Text color={light ? 'whiteAlpha.700' : 'gray.500'} fontSize="sm" textAlign="center">
            No hay eventos en los meses seleccionados.
          </Text>
        </Box>
      ) : null}
    </VStack>
  );
}

